/**
 * Design JSON V2 — typedefs + pure factory constructors.
 *
 * These builders produce well-formed, structurally-complete Design JSON V2
 * skeletons. They are the canonical way to construct any V2 structure, so the
 * shape lives in exactly one place. Every factory is PURE and DETERMINISTIC:
 * no randomness, no timestamps, no I/O. Callers (generator, compiler, tests)
 * supply ids; factories default them to null so output stays reproducible.
 *
 * Geometry (x/y/width/height) is intentionally OPTIONAL on nodes — V2 is an
 * intent language and the compiler's auto-layout resolver supplies geometry
 * when it is omitted. Factories never invent positions.
 */

import {
  SCHEMA_ID, SCHEMA_VERSION, ORIENTATIONS, LAYER_BANDS,
} from './constants';

// ── Geometry helpers ─────────────────────────────────────────────────────────

/**
 * Derive canvas orientation from dimensions.
 * @param {number} width
 * @param {number} height
 * @returns {'portrait'|'landscape'|'square'}
 */
export const deriveOrientation = (width, height) => {
  if (width === height) return ORIENTATIONS[2]; // square
  return width > height ? ORIENTATIONS[1] : ORIENTATIONS[0]; // landscape : portrait
};

// ── Tokens (the brand kit) ───────────────────────────────────────────────────

/**
 * @typedef {Object} TypeStyleToken
 * @property {string|null} font   Family name.
 * @property {number} weight
 * @property {number} step        Modular-scale exponent (not raw px).
 * @property {number} leading     Line-height multiplier.
 * @property {number} tracking    Letter spacing.
 * @property {'none'|'upper'|'lower'|'title'|'sentence'} case
 */

/**
 * @typedef {Object} Tokens
 * @property {Object<string, (string|null)>} color   Semantic role → value/fillRef.
 * @property {Object<string, TypeStyleToken>} type
 * @property {Object<string, number>} space          Scale step → px.
 * @property {Object<string, number>} radius
 * @property {Object<string, Object>} elevation      Named shadow presets.
 * @property {Object<string, Object>} stroke
 * @property {Object<string, Object>} effect         Named effect presets.
 */

/**
 * Build a structurally-complete default token set (all groups present, values
 * left neutral/empty for the producer to fill). A standard modular space scale
 * is provided as a structural convention, not a creative decision.
 * @param {Partial<Tokens>} [overrides]
 * @returns {Tokens}
 */
export const createTokens = (overrides = {}) => ({
  color: {
    primary: null, secondary: null, accent: null, surface: null,
    onSurface: null, muted: null, success: null, warning: null,
    ...(overrides.color || {}),
  },
  type: {
    display: { font: null, weight: 800, step: 6, leading: 1.05, tracking: -1, case: 'none' },
    h1: { font: null, weight: 700, step: 4, leading: 1.1, tracking: -0.5, case: 'none' },
    h2: { font: null, weight: 600, step: 3, leading: 1.2, tracking: 0, case: 'none' },
    h3: { font: null, weight: 600, step: 2, leading: 1.25, tracking: 0, case: 'none' },
    body: { font: null, weight: 400, step: 0, leading: 1.5, tracking: 0, case: 'none' },
    caption: { font: null, weight: 400, step: -1, leading: 1.4, tracking: 0.2, case: 'none' },
    overline: { font: null, weight: 600, step: -1, leading: 1.4, tracking: 1.5, case: 'upper' },
    ...(overrides.type || {}),
  },
  space: { xs: 4, sm: 8, md: 16, lg: 24, xl: 40, '2xl': 64, '3xl': 96, ...(overrides.space || {}) },
  radius: { none: 0, sm: 4, md: 12, lg: 24, pill: 999, ...(overrides.radius || {}) },
  elevation: { ...(overrides.elevation || {}) },
  stroke: { ...(overrides.stroke || {}) },
  effect: { ...(overrides.effect || {}) },
});

// ── Fills, strokes, effects, masks ───────────────────────────────────────────

/**
 * @typedef {Object} FillLayer
 * @property {string} kind      One of FILL_KINDS.
 * @property {number} opacity
 * @property {string} blend
 */

/**
 * Build one fill layer of the given kind. Extra kind-specific props (stops,
 * angle, src, focalPoint, pattern, …) are spread in verbatim.
 * @param {string} kind
 * @param {Object} [props]
 * @returns {FillLayer}
 */
export const createFill = (kind, props = {}) => ({ kind, opacity: 1, blend: 'normal', ...props });

/**
 * @typedef {Object} Stroke
 * @property {number} width
 * @property {'inside'|'center'|'outside'} align
 * @property {'solid'|'dashed'|'dotted'} style
 * @property {(string|FillLayer|null)} fill   Token ref, color, or fill layer.
 */

/** @param {Partial<Stroke>} [props] @returns {Stroke} */
export const createStroke = (props = {}) => ({
  width: 0, align: 'center', style: 'solid', fill: null, ...props,
});

/**
 * @typedef {Object} Effect
 * @property {string} kind   One of EFFECT_KINDS.
 */

/** @param {string} kind @param {Object} [props] @returns {Effect} */
export const createEffect = (kind, props = {}) => ({ kind, ...props });

/**
 * @typedef {Object} Mask
 * @property {string} kind   One of MASK_KINDS.
 */

/** @param {string} kind @param {Object} [props] @returns {Mask} */
export const createMask = (kind, props = {}) => ({ kind, ...props });

// ── Node meta: constraints + editability ─────────────────────────────────────

/**
 * @typedef {Object} Constraints
 * @property {'left'|'right'|'center'|'stretch'|'scale'} h
 * @property {'top'|'bottom'|'center'|'stretch'|'scale'} v
 */

/** @param {Partial<Constraints>} [props] @returns {Constraints} */
export const createConstraints = (props = {}) => ({ h: 'left', v: 'top', ...props });

/**
 * @typedef {Object} Editable
 * @property {boolean} content
 * @property {boolean} style
 * @property {boolean} position
 * @property {boolean} size
 * @property {boolean} swappable   Image/content slot may be replaced.
 * @property {boolean} locked
 */

/** @param {Partial<Editable>} [props] @returns {Editable} */
export const createEditable = (props = {}) => ({
  content: true, style: true, position: true, size: true, swappable: true, locked: false, ...props,
});

// ── Auto-layout ──────────────────────────────────────────────────────────────

/**
 * @typedef {Object} AutoLayout
 * @property {'stack'|'row'|'grid'|'free'} type
 * @property {(string|number)} gap      Space token ref or px.
 * @property {(string|number)} pad      Padding token ref or px.
 * @property {'start'|'center'|'end'|'stretch'|'baseline'} align
 * @property {'start'|'center'|'end'|'between'|'around'|'evenly'} distribute
 * @property {'hug'|'fill'|'fixed'} sizing
 * @property {(Object|null)} grid       Grid spec when type==='grid'.
 */

/** @param {Partial<AutoLayout>} [props] @returns {AutoLayout} */
export const createAutoLayout = (props = {}) => ({
  type: 'stack', gap: 'md', pad: 0, align: 'start', distribute: 'start', sizing: 'hug', grid: null, ...props,
});

// ── Nodes ────────────────────────────────────────────────────────────────────

/**
 * @typedef {Object} NodeBase
 * @property {(string|null)} id
 * @property {string} node        One of NODE_KINDS.
 * @property {(string|null)} role  Semantic role (headline, cta, logo, …).
 * @property {string} layer       One of LAYER_BANDS.
 * @property {(number|null)} x
 * @property {(number|null)} y
 * @property {(number|null)} width
 * @property {(number|null)} height
 * @property {number} rotation
 * @property {FillLayer[]} fills
 * @property {Stroke} stroke
 * @property {Effect[]} effects
 * @property {(Mask|null)} mask
 * @property {Constraints} constraints
 * @property {Editable} editable
 */

const baseNode = (node, props = {}) => ({
  id: props.id ?? null,
  node,
  role: props.role ?? null,
  layer: LAYER_BANDS.includes(props.layer) ? props.layer : 'content',
  x: props.x ?? null,
  y: props.y ?? null,
  width: props.width ?? null,
  height: props.height ?? null,
  rotation: props.rotation ?? 0,
  fills: props.fills ?? [],
  stroke: props.stroke ?? createStroke(),
  effects: props.effects ?? [],
  mask: props.mask ?? null,
  constraints: props.constraints ?? createConstraints(),
  editable: props.editable ?? createEditable(),
});

/**
 * Text object node. Style is a type-token reference; content is the copy.
 * @param {Object} props
 * @returns {Object}
 */
export const createTextNode = (props = {}) => ({
  ...baseNode('object', props),
  type: 'text',
  text: props.text ?? '',
  style: props.style ?? 'body',       // type token ref
  color: props.color ?? 'onSurface',  // color token ref
  align: props.align ?? 'left',
  runs: props.runs ?? null,           // optional rich-text runs
});

/**
 * Shape object node.
 * @param {Object} props
 * @returns {Object}
 */
export const createShapeNode = (props = {}) => ({
  ...baseNode('object', props),
  type: 'shape',
  shape: props.shape ?? 'rect',
  cornerRadius: props.cornerRadius ?? 0,
});

/**
 * Image object node. `query` is a semantic search term (no asset fetched here).
 * @param {Object} props
 * @returns {Object}
 */
export const createImageNode = (props = {}) => ({
  ...baseNode('object', props),
  type: 'image',
  query: props.query ?? null,
  src: props.src ?? null,
  fit: props.fit ?? 'cover',
  focalPoint: props.focalPoint ?? { x: 0.5, y: 0.5 },
  crop: props.crop ?? null,
});

/**
 * Icon object node.
 * @param {Object} props
 * @returns {Object}
 */
export const createIconNode = (props = {}) => ({
  ...baseNode('object', props),
  type: 'icon',
  icon: props.icon ?? null,
  color: props.color ?? 'onSurface',
});

/**
 * Vector / illustration object node (path or library reference).
 * @param {Object} props
 * @returns {Object}
 */
export const createVectorNode = (props = {}) => ({
  ...baseNode('object', props),
  type: 'vector',
  path: props.path ?? null,
  ref: props.ref ?? null,
});

/**
 * Line / divider object node.
 * @param {Object} props
 * @returns {Object}
 */
export const createLineNode = (props = {}) => ({
  ...baseNode('object', props),
  type: 'line',
  orientation: props.orientation ?? 'horizontal',
  thickness: props.thickness ?? 1,
});

/**
 * Group node — an auto-layout container of child nodes.
 * @param {Object} props
 * @returns {Object}
 */
export const createGroupNode = (props = {}) => ({
  ...baseNode('group', props),
  layout: props.layout ?? createAutoLayout(),
  children: props.children ?? [],
});

/**
 * Frame node — a shaped clipping container with a single child (e.g. an image
 * cropped to a shape). Independent crop/focal point live on the child image.
 * @param {Object} props
 * @returns {Object}
 */
export const createFrameNode = (props = {}) => ({
  ...baseNode('frame', props),
  clip: props.clip ?? 'rect',        // shape the frame clips to
  cornerRadius: props.cornerRadius ?? 0,
  child: props.child ?? null,
});

/**
 * Component instance node — references a library/component definition and fills
 * its slots/props. The compiler's component-expander turns it into primitives.
 * @param {Object} props
 * @returns {Object}
 */
export const createComponentInstance = (props = {}) => ({
  ...baseNode('component', props),
  component: props.component ?? null, // component id/ref
  variant: props.variant ?? null,
  props: props.props ?? {},
  slots: props.slots ?? {},
});

// ── Structure: sections, background, canvas, pages, variants, document ────────

/**
 * @typedef {Object} Section
 * @property {(string|null)} id
 * @property {string} role     One of SECTION_ROLES.
 * @property {AutoLayout} layout
 * @property {Object[]} nodes
 */

/** @param {Object} [props] @returns {Section} */
export const createSection = (props = {}) => ({
  id: props.id ?? null,
  role: props.role ?? 'content',
  layout: props.layout ?? createAutoLayout(),
  nodes: props.nodes ?? [],
});

/**
 * @typedef {Object} Grid
 * @property {number} cols
 * @property {(number|null)} rows
 * @property {(string|number)} gutter
 * @property {(string|number)} margin
 */

/** @param {Partial<Grid>} [props] @returns {Grid} */
export const createGrid = (props = {}) => ({ cols: 12, rows: null, gutter: 'md', margin: 'lg', ...props });

/**
 * @typedef {Object} Canvas
 * @property {number} width
 * @property {number} height
 * @property {'px'|'mm'} unit
 * @property {'portrait'|'landscape'|'square'} orientation
 * @property {number} bleed
 * @property {number} safeArea   Inset fraction (0–1).
 * @property {Grid} grid
 */

/**
 * @param {Object} props  Requires width & height.
 * @returns {Canvas}
 */
export const createCanvas = (props = {}) => {
  const width = props.width ?? 1080;
  const height = props.height ?? 1080;
  return {
    width, height,
    unit: props.unit ?? 'px',
    orientation: props.orientation ?? deriveOrientation(width, height),
    bleed: props.bleed ?? 0,
    safeArea: props.safeArea ?? 0.06,
    grid: props.grid ?? createGrid(),
  };
};

/**
 * @typedef {Object} Page
 * @property {(string|null)} id
 * @property {Canvas} canvas
 * @property {FillLayer[]} background   Layered background stack.
 * @property {string[]} layers          Declared z-band order.
 * @property {Section[]} sections
 */

/** @param {Object} [props] @returns {Page} */
export const createPage = (props = {}) => ({
  id: props.id ?? null,
  canvas: props.canvas ?? createCanvas(),
  background: props.background ?? [],
  layers: props.layers ?? [...LAYER_BANDS],
  sections: props.sections ?? [],
});

/**
 * @typedef {Object} Variant
 * @property {string} size       "WxH" target.
 * @property {'constraints'|'reflow'} reflow
 */

/** @param {Object} [props] @returns {Variant} */
export const createVariant = (props = {}) => ({ size: props.size ?? null, reflow: props.reflow ?? 'constraints' });

/**
 * @typedef {Object} DesignDocument
 * @property {string} schema
 * @property {number} version
 * @property {Object} meta
 * @property {Tokens} tokens
 * @property {Object[]} components   Reusable component definitions.
 * @property {Variant[]} variants
 * @property {Page[]} pages
 */

/**
 * Build a complete, structurally-valid Design JSON V2 document skeleton.
 * @param {Object} [props]
 * @returns {DesignDocument}
 */
export const createDesignDocument = (props = {}) => ({
  schema: SCHEMA_ID,
  version: SCHEMA_VERSION,
  meta: {
    title: props.title ?? null,
    documentType: props.documentType ?? null,
    category: props.category ?? null,
    purpose: props.purpose ?? null,
    genome: props.genome ?? null,           // Style Genome id (model built in Phase 3)
    researchVersion: props.researchVersion ?? null,
    createdBy: props.createdBy ?? 'ai',
    ...(props.meta || {}),
  },
  tokens: props.tokens ?? createTokens(),
  components: props.components ?? [],
  variants: props.variants ?? [],
  pages: props.pages ?? [createPage()],
});
